import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { QueryClientProvider } from '@tanstack/react-query'
import { Popover } from 'bootstrap'
import useConfigurationData from '../../hooks/useConfigurationData'
import useRealTimeValidation from '../../hooks/useRealTimeValidation'
import { transformFormDataForRegulator } from '../../utils/formDataTransformers'
import { compareVersions } from '../../utils/versionUtils'
import PasswordModal from '../configuration/PasswordModal'
import ConfirmationModal from '../configuration/ConfirmationModal'
import AlternatorSection from '../configuration/AlternatorSection'
import BatterySection from '../configuration/BatterySection'
import NetworkSection from '../configuration/NetworkSection'
import ImportExportSection from '../configuration/ImportExportSection'
import { PageContainer, PageHeader, StatusIndicator } from '../common'
import useAlert from '../../hooks/useAlert.js'
import { getConfigurationStatus } from '../../utils/statusUtils.js'
import { useInternetConnectivity } from '../../hooks/useInternetConnectivity.js'
import '../../styles/animations.css'

function ConfigurationPage({ queryClient: _queryClient }) {
  // Memoized utility function to clean form data (performance optimization)
  const cleanFormData = useMemo(() => (data) => {
    if (!data) return {}
    return Object.keys(data).reduce((acc, key) => {
      const value = data[key]
      
      // Special handling for battery voltage fields - provide defaults if empty/null
      if (key === 'batteryVoltage' || key === 'batteryVoltage2') {
        acc[key] = (value === null || value === undefined || value === 'null' || value === '') ? '12' : value
      } else {
        // Convert null, undefined, or 'null' string to empty string for form inputs using modern nullish coalescing
        acc[key] = (value === null || value === undefined || value === 'null') ? '' : value
      }
      return acc
    }, {})
  }, [])

  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [deviceInfo, setDeviceInfo] = useState(null)
  const { alertMessage, alertType, showAlert, clearAlert } = useAlert()
  const { formData, isLoading, error, saveConfiguration, isSaving, lastSavedData } = useConfigurationData()
  
  // Cloud connectivity hooks
  const { isConnected: isInternetConnected } = useInternetConnectivity()
  
  // Get serial number from existing deviceInfo state
  const serialNumber = deviceInfo?.cpuInfo || null
  const { register, watch, formState: { errors, isDirty: formIsDirty }, reset, setValue, unregister } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    shouldUseNativeValidation: false,
  })
    // Real-time validation
  const {
    getFieldError,
    touchField,
    validateAllFields,
    resetValidation,
    isValid,
    errorCount,
    scrollToFirstError,
  } = useRealTimeValidation(watch(), formIsDirty, deviceInfo)

  // Reset form when data loads - handle null values properly
  useEffect(() => {
    if (formData) {
      const cleanedData = cleanFormData(formData)
      
      reset(cleanedData)
      
      // Force update radio buttons after reset - workaround for React Hook Form radio button issue
      setTimeout(() => {
        if (cleanedData.numBatteriesManaged === 'numBatteriesManaged2') {
          setValue('numBatteriesManaged', 'numBatteriesManaged2')
        } else if (cleanedData.numBatteriesManaged === 'numBatteriesManaged1' || !cleanedData.numBatteriesManaged) {
          // Ensure single battery is selected when no specific selection or explicitly set to single
          setValue('numBatteriesManaged', 'numBatteriesManaged1')
        }
      }, 100)
      
      // Show password modal if configPassword is set (using modern nullish coalescing)
      if (cleanedData.configPassword?.trim()) {
        setShowPasswordModal(true)
      }
    }
  }, [formData, reset, setValue, cleanFormData])

  // Fetch device info for components that need device type information
  useEffect(() => {
    const fetchDeviceInfo = async () => {
      try {
        const response = await fetch('/getDeviceInfo')
        if (response.ok) {
          const data = await response.json()
          setDeviceInfo(data)
        } else {
          console.error('Failed to fetch device info')
        }
      } catch (error) {
        console.error('Error fetching device info:', error)
      }
    }

    fetchDeviceInfo()
  }, [])// Initialize Bootstrap popovers with DRY principles
  const initializePopovers = () => {
    try {
      const popoverConfig = {
        trigger: 'click',
        placement: 'auto',
        html: true,
        sanitize: false,
        container: 'body',
      }

      const setupClickOutside = (popover, triggerEl) => {
        const handleClickOutside = (event) => {
          const popoverElement = document.querySelector('.popover')
          if (popoverElement && 
              !popoverElement.contains(event.target) && 
              !triggerEl.contains(event.target)) {
            popover.hide()
            document.removeEventListener('click', handleClickOutside)
          }
        }
        setTimeout(() => document.addEventListener('click', handleClickOutside), 100)
      }

      document.querySelectorAll('[data-bs-toggle="popover"]').forEach(triggerEl => {
        const existingPopover = Popover.getInstance(triggerEl)
        if (existingPopover) existingPopover.dispose()
        
        const popover = new Popover(triggerEl, popoverConfig)
        triggerEl.addEventListener('shown.bs.popover', () => setupClickOutside(popover, triggerEl))
      })
    } catch {
      // Bootstrap Popover initialization failed - silently continue
    }
  }

  // Consolidated popover initialization - re-initialize when form data or conditional fields change
  const watchedConditionalFields = watch([
    'shuntInstalled', 'shuntInstalled2', 'tempConnected', 'tempConnected2', 
    'postCvSwitch', 'postCvSwitch2', 'chargeCutoffInstalled', 'chargeCutoffInstalled2',
    'statorInstalled', 'statorInstalled2', 'rpmReduceField', 'rpmReduceField2',
    'altShuntInstalled', 'altShuntInstalled2',
  ])
  useEffect(() => {
    const timeoutId = setTimeout(initializePopovers, 100)
    return () => clearTimeout(timeoutId)
  }, [
    formData, // Re-initialize when formData changes
    watchedConditionalFields, // Watch the conditional fields as an array
  ])

  // Watch specific fields needed for alternator count changes
  const alternatorAndBatteryConfig = watch(['numAlternatorsInstalled', 'numBatteriesManaged'])
  
  // Handle alternator count changes - reset battery bank selection when switching to single alternator
  useEffect(() => {
    const [numAlternatorsInstalled, numBatteriesManaged] = alternatorAndBatteryConfig
    
    // Reset battery selection when switching to single alternator or when numBatteriesManaged is set to 'Two' but we only have one alternator
    if (formData && numAlternatorsInstalled === 'numAlternatorsInstalled1') {
      // Case 1: User was using 2 alternators and switched to 1
      const wasUsingTwoAlternators = formData.numAlternatorsInstalled === 'numAlternatorsInstalled2'
      
      // Case 2: Battery management is set to 'Two' but we only have one alternator
      const batterySetToTwoWithOneAlt = numBatteriesManaged === 'numBatteriesManaged2'
      
      if (wasUsingTwoAlternators || batterySetToTwoWithOneAlt) {
        // Step 1: Switch to single battery management (this will trigger battery 2 cleanup)
        setValue('numBatteriesManaged', 'numBatteriesManaged1')
        
        // IMPORTANT: Remove numBatteriesManaged2 field from form state when switching to single alternator
        // React Hook Form keeps registered fields even when conditionally rendered
        setValue('numBatteriesManaged2', undefined)
        unregister('numBatteriesManaged2')
        
        // Note: AlternatorSection handles its own alternator 2 checkbox cleanup
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alternatorAndBatteryConfig, setValue, formData])

  const handleFieldBlur = useCallback((fieldName, value) => {
    touchField(fieldName)
    // Update form data through setValue for React Hook Form
    setValue(fieldName, value)
  }, [touchField, setValue]);  const handleFormSubmit = (event) => {
    // Prevent default form submission using modern optional chaining
    event?.preventDefault()
    
    try {
      // Run comprehensive validation and get the result
      const isFormValid = validateAllFields()
      
      if (!isFormValid) {
        // Scroll to first validation error (no alert message needed)
        setTimeout(() => {
          scrollToFirstError()
        }, 100)
        
        return
      }
      
      // Clear any previous error messages
      clearAlert()
      
      // If validation passes, show the confirmation modal
      setShowConfirmationModal(true)
      
    } catch (error) {
      console.error('Error accessing form data:', error)
      showAlert(`Error accessing form data: ${error.message}`, 'danger', 0)
    }
  }

  const handleReset = () => {
    // Reset React Hook Form to last saved data
    if (formData) {
      const cleanedData = cleanFormData(formData)
      reset(cleanedData)
      
      // Clear validation errors and touched state after reset
      resetValidation()
    }
  }
  const handleConfirmSave = () => {
    const formDataValues = watch()
    
    // Use shared transformation utility for regulator.js compatibility
    // Exclude UI state fields from uploads to prevent duplicate metadata fields
    const transformedData = transformFormDataForRegulator(formDataValues, { excludeUiState: true })
    
    // IMPORTANT: Handle numBatteriesManaged2 field correctly
    // When user selects "Two" batteries, we need to preserve numBatteriesManaged2 in the config
    if (formDataValues.numBatteriesManaged === 'numBatteriesManaged2') {
      transformedData.numBatteriesManaged2 = 'numBatteriesManaged2'
    } else {
      // IMPORTANT: Remove numBatteriesManaged2 when user selects "One" battery
      // This ensures the field is cleaned up when switching from two batteries back to one
      delete transformedData.numBatteriesManaged2
    }
    
    // IMPORTANT: Also clean up numBatteriesManaged2 field if only one alternator is configured
    // This ensures the field doesn't persist in the config when switching from 2 to 1 alternator
    if (transformedData.numAlternatorsInstalled === 'numAlternatorsInstalled1') {
      delete transformedData.numBatteriesManaged2
    }
    
    // IMPORTANT: Clean up fieldPolarity fields for hardware >= 1.1.0
    // These fields are not used on newer hardware (auto field polarity detection)
    if (deviceInfo?.regulatorHardwareVersion && compareVersions(deviceInfo.regulatorHardwareVersion, '1.1.0') >= 0) {
      delete transformedData.fieldPolarity
      delete transformedData.fieldPolarity2
    }
    
    saveConfiguration(transformedData)
    showAlert('Configuration saved successfully!')
    
    // Reset form dirty state after successful save
    reset(formDataValues, { keepValues: true })
  };  const handlePasswordSubmit = () => {
    // Password was verified successfully in the modal - just close it
    setShowPasswordModal(false)
  }

  const handleImportConfiguration = (importedData) => {
    // Validate and apply imported configuration using modern object validation
    if (importedData && typeof importedData === 'object' && !Array.isArray(importedData)) {
      reset(importedData)
      showAlert('Configuration imported successfully!')
    } else {
      showAlert('Invalid configuration file format.', 'danger', 8000)
    }
  }

  // Memoized section components array for performance optimization
  const sectionComponents = useMemo(() => [
    AlternatorSection, 
    BatterySection, 
    NetworkSection,
  ], [])

  // Memoized section props for performance optimization  
  const sectionProps = useMemo(() => ({
    register,
    errors,
    watch,
    onBlur: handleFieldBlur,
    getFieldError,
    setValue,
    deviceInfo,
    isInternetConnected,
    serialNumber,
  }), [register, errors, watch, handleFieldBlur, getFieldError, setValue, deviceInfo, isInternetConnected, serialNumber])

  if (isLoading) {
    return (
      <div className="container mb-5 mt-3">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading...</p>
        </div>
      </div>
    )
  }

  // Handle configuration loading errors
  if (error) {
    return (
      <div className="container mb-5 mt-3">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Configuration Loading Error
          </h4>
          <p>Failed to load the regulator configuration: {error.message}</p>
          <hr />
          <button 
            className="btn btn-outline-danger"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Configuration status using utility function
  const configStatusConfig = getConfigurationStatus(
    !isValid && errorCount > 0,
    errorCount,
    formIsDirty,
    isSaving,
    lastSavedData,
  )

  return (
    <PageContainer hasFixedFooter>
      {/* Modals */}
      <PasswordModal 
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordSubmit}
        configPassword={formData?.configPassword}
      />
      
      <ConfirmationModal 
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmSave}
      />

      <PageHeader 
        title="Configuration"
        statusIndicator={
          <StatusIndicator 
            status={configStatusConfig.status}
            text={configStatusConfig.text}
            animation={configStatusConfig.animation}
          />
        }
        actions={
          isSaving && (
            <div className="d-flex align-items-center">
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              <small className="text-muted">Saving...</small>
            </div>
          )
        }
      />
      
      {/* First-time setup banner - Modern Card Style */}
      {!lastSavedData && (
        <div 
          className="card shadow-sm border-0 mb-3" 
          style={{ 
            background: 'linear-gradient(to right, #d1ecf1, #bee5eb)',
            transition: 'all 0.3s ease',
          }}
        >
          <div className="card-header fw-bold border-0" style={{ background: 'transparent' }}>
            <i className="bi bi-gear-fill me-2 text-info fs-4" aria-hidden="true"></i>
            <span>First-Time Setup</span>
          </div>
          <div className="card-body" style={{ paddingBottom: '0.75rem' }}>
            <div style={{ marginBottom: '-0.5rem' }}>
              <p className="mb-2">
                Welcome! This appears to be the first time configuring your regulator.
              </p>
              <p className="mb-2 fw-semibold">
                Please review and adjust the settings according to your system requirements, then save the configuration.
              </p>
            </div>
          </div>
        </div>
      )}
      <form noValidate className="mb-3">{/* Remove handleSubmit from form */}        {/* Create reusable section props using modern component mapping with performance optimization */}
        {sectionComponents.map((Component, index) => (
          <Component 
            key={Component.name || index}
            {...sectionProps}
          />
        ))}

        <ImportExportSection
          formData={watch()}
          onImport={handleImportConfiguration}
          isLoading={isLoading}
        />
      </form>

      {/* Primary Action - Save Configuration */}
      <div className="position-sticky bottom-0 bg-white border-top shadow-lg py-3 mt-4" style={{ zIndex: 1000 }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            {/* Status Section */}
            <div>
              {!isValid && errorCount > 0 && (
                <div className="d-flex align-items-center text-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <span className="fw-semibold me-3">
                    {errorCount} validation error{errorCount !== 1 ? 's' : ''} found
                  </span>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-danger"
                    onClick={scrollToFirstError}
                    title="Scroll to first error"
                  >
                    <i className="bi bi-arrow-up-circle me-1"></i>
                    Go to first error
                  </button>
                </div>
              )}
              {formIsDirty && isValid && (
                <div className="d-flex align-items-center text-primary">
                  <i className="bi bi-pencil-square me-2"></i>
                  <span className="fw-semibold">You have unsaved changes</span>
                </div>
              )}
              {!formIsDirty && isValid && formData && (
                <div className="d-flex align-items-center text-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <span className="fw-semibold">Configuration up to date</span>
                </div>
              )}
              {alertMessage && (
                <div className={`d-flex align-items-center ${alertType === 'success' ? 'text-success' : 'text-danger'}`}>
                  <i className={`bi ${alertType === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                  <span className="fw-semibold">{alertMessage}</span>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="d-flex gap-3 align-items-center">
              {formIsDirty && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={handleReset}
                  title="Reset to last saved values"
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Reset Changes
                </button>
              )}
              
              <button 
                type="button" 
                className={`btn btn-lg ${!isValid && errorCount > 0 ? 'btn-outline-danger' : 'btn-primary'}`}
                disabled={isSaving || (!isValid && errorCount > 0)}
                onClick={handleFormSubmit}
                style={{ minWidth: '200px', fontSize: '1rem' }}
                title={!isValid && errorCount > 0 ? 'Please fix validation errors before saving' : 'Save configuration'}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-floppy me-2"></i>
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  )
}

// Wrapper component that provides QueryClient context if needed
function ConfigurationPageWithQueryClient(props) {
  const { queryClient, ...otherProps } = props
  
  // If queryClient is provided (from connect app), wrap with provider
  if (queryClient) {
    return (
      <QueryClientProvider client={queryClient}>
        <ConfigurationPage {...otherProps} />
      </QueryClientProvider>
    )
  }
  
  // Otherwise, assume context is already provided (express app)
  return <ConfigurationPage {...otherProps} />
}

export default React.memo(ConfigurationPageWithQueryClient)
